/**
 * A small trend line, or the space where one will be.
 *
 * The mockup puts a sparkline and a "vs yesterday" delta under every count.
 * Most of them have nothing to draw yet: the portal stores what is true now
 * and never what was true yesterday, so three of the four counts have no
 * history to plot.
 *
 * Rather than draw an invented line, this keeps the space and the baseline and
 * draws nothing. The layout is the mockup's either way, so the day a counts
 * table exists each tile starts reading without anything moving. A drawn trend
 * on a clinical dashboard is worse than an absent one: nobody squints at a
 * blank area and concludes the backlog is falling.
 *
 * `points` is oldest first. Two points are enough to draw a line; fewer is not
 * a trend and is treated as none.
 */
export function Sparkline({
  points,
  tone = "gold",
}: {
  points: number[] | null;
  tone?: "gold" | "green" | "red" | "blue";
}) {
  const W = 86;
  const H = 26;
  const enough = points !== null && points.length >= 2;

  let d = "";
  if (enough) {
    const low = Math.min(...points);
    const high = Math.max(...points);
    const span = high - low || 1;
    d = points
      .map((value, i) => {
        const x = (i / (points.length - 1)) * W;
        // A flat series sits on the middle rather than on the floor, so "no
        // change" does not read as "fallen to nothing".
        const y = high === low ? H / 2 : H - ((value - low) / span) * (H - 4) - 2;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }

  return (
    <svg
      className={`spark ${tone}`}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/* The baseline is always there. It is the floor of the chart, not a
          reading, so it says nothing when there is nothing to say. */}
      <line className="spark-base" x1="0" y1={H - 1} x2={W} y2={H - 1} />
      {enough && <path className="spark-line" d={d} />}
    </svg>
  );
}
